import React, { useEffect, useState, useMemo } from 'react';
import { BarChart3, TrendingUp, AlertTriangle, Download, Loader2, Smile, Meh, Frown, Activity } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

const PERIODS = [
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
  { value: 'year', label: '12 mois' },
  { value: 'all', label: 'Tout' },
];

function npsColor(nps) {
  if (nps === null || nps === undefined) return '#8E8FA5';
  if (nps >= 50) return '#0F766E';
  if (nps >= 0) return '#14B8A6';
  if (nps >= -30) return '#F59E0B';
  return '#EF4444';
}

function npsBadge(nps) {
  if (nps === null || nps === undefined) return '—';
  return `${nps > 0 ? '+' : ''}${nps}`;
}

function MetricCard({ icon: Icon, label, value, hint, color = '#4F46E5' }) {
  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-white p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-[12px] text-[#6B6C85]">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        {label}
      </div>
      <div className="text-[26px] font-bold leading-none" style={{ color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-[#8E8FA5]">{hint}</div>}
    </div>
  );
}

function NPSGauge({ nps, samples }) {
  if (nps === null || nps === undefined) {
    return (
      <div className="rounded-xl border border-[#EFF0F6] bg-white p-5 flex flex-col items-center justify-center min-h-[170px]">
        <Activity className="w-8 h-8 text-[#8E8FA5] mb-2" />
        <div className="text-[13px] text-[#6B6C85] text-center">Pas encore de données NPS</div>
        <div className="text-[11px] text-[#8E8FA5] mt-1 text-center">Diffusez des questionnaires de satisfaction comportant une question d'échelle.</div>
      </div>
    );
  }
  // Map NPS [-100..100] to [0..1]
  const pct = (nps + 100) / 200;
  const color = npsColor(nps);
  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-white p-5">
      <div className="flex items-center gap-2 text-[12px] text-[#6B6C85] mb-3">
        <TrendingUp className="w-3.5 h-3.5" /> Net Promoter Score
      </div>
      <div className="flex items-end gap-3">
        <div className="text-[44px] font-bold leading-none" style={{ color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {nps > 0 ? '+' : ''}{nps}
        </div>
        <div className="text-[11px] text-[#8E8FA5] mb-1">sur {samples} réponse{samples > 1 ? 's' : ''}</div>
      </div>
      <div className="mt-4 relative h-2 rounded-full bg-gradient-to-r from-[#EF4444] via-[#F59E0B] to-[#0F766E]">
        <div
          className="absolute -top-1 w-4 h-4 rounded-full bg-white border-2 shadow-md"
          style={{ left: `calc(${pct * 100}% - 8px)`, borderColor: color }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-[#8E8FA5] mt-1.5">
        <span>-100</span><span>0</span><span>+100</span>
      </div>
    </div>
  );
}

function PromoterBreakdown({ promoters, passives, detractors, samples }) {
  if (!samples) return null;
  const p = (promoters / samples) * 100;
  const ps = (passives / samples) * 100;
  const d = (detractors / samples) * 100;
  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-white p-5">
      <div className="text-[12px] text-[#6B6C85] mb-3 flex items-center gap-2">
        <Smile className="w-3.5 h-3.5 text-[#0F766E]" /> Répartition des répondants
      </div>
      <div className="flex h-3 rounded-full overflow-hidden">
        <div className="bg-[#0F766E]" style={{ width: `${p}%` }} title={`Promoteurs ${promoters}`} />
        <div className="bg-[#F59E0B]" style={{ width: `${ps}%` }} title={`Passifs ${passives}`} />
        <div className="bg-[#EF4444]" style={{ width: `${d}%` }} title={`Détracteurs ${detractors}`} />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-[12px]">
        <div className="flex items-center gap-1.5"><Smile className="w-3.5 h-3.5 text-[#0F766E]" /> <span className="font-semibold">{promoters}</span> Promoteurs</div>
        <div className="flex items-center gap-1.5"><Meh className="w-3.5 h-3.5 text-[#F59E0B]" /> <span className="font-semibold">{passives}</span> Passifs</div>
        <div className="flex items-center gap-1.5"><Frown className="w-3.5 h-3.5 text-[#EF4444]" /> <span className="font-semibold">{detractors}</span> Détracteurs</div>
      </div>
    </div>
  );
}

function Table({ headers, rows, empty }) {
  if (!rows.length) {
    return (
      <div className="text-center py-8 text-[13px] text-[#8E8FA5]">{empty}</div>
    );
  }
  return (
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="text-left text-[#6B6C85] text-[11.5px] uppercase tracking-wide border-b border-[#EFF0F6]">
          {headers.map((h, i) => <th key={i} className={`py-2.5 px-3 ${i === 0 ? 'pl-0' : ''} ${i === headers.length - 1 ? 'pr-0 text-right' : ''}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-[#F3F4F8] hover:bg-[#FAFAFE]">
            {r.map((cell, j) => <td key={j} className={`py-2.5 px-3 ${j === 0 ? 'pl-0 font-semibold text-[#1A1B2E]' : 'text-[#4A4B63]'} ${j === r.length - 1 ? 'pr-0 text-right' : ''}`}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function QualiopiDashboard() {
  const [period, setPeriod] = useState('year');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/qualiopi/dashboard?period=${period}`)
      .then(({ data }) => { if (!cancel) setData(data); })
      .catch(() => { if (!cancel) toast({ title: 'Erreur', description: 'Impossible de charger le dashboard', variant: 'destructive' }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [period]);

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      const { data: blob } = await api.get(`/qualiopi/dashboard/pdf?period=${period}`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-qualiopi-${period}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Rapport téléchargé', description: 'Prêt pour audit Qualiopi' });
    } catch {
      toast({ title: 'Erreur', description: "Échec de l'export PDF", variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const g = data?.global;

  const programmeRows = useMemo(() => (data?.by_programme || []).map((p) => [
    p.name,
    p.sent,
    p.received,
    <span key="rr" className="font-semibold" style={{ color: p.response_rate >= 60 ? '#0F766E' : p.response_rate >= 30 ? '#F59E0B' : '#EF4444' }}>{p.response_rate}%</span>,
    <span key="nps" className="font-semibold" style={{ color: npsColor(p.nps) }}>{npsBadge(p.nps)}</span>,
    p.satisfaction_avg !== null && p.satisfaction_avg !== undefined ? `${p.satisfaction_avg}%` : '—',
  ]), [data]);

  const trainerRows = useMemo(() => (data?.by_trainer || []).map((t) => [
    t.name,
    t.sessions_count,
    t.sent,
    t.received,
    <span key="rr" className="font-semibold" style={{ color: t.response_rate >= 60 ? '#0F766E' : t.response_rate >= 30 ? '#F59E0B' : '#EF4444' }}>{t.response_rate}%</span>,
    <span key="nps" className="font-semibold" style={{ color: npsColor(t.nps) }}>{npsBadge(t.nps)}</span>,
    t.satisfaction_avg !== null && t.satisfaction_avg !== undefined ? `${t.satisfaction_avg}%` : '—',
  ]), [data]);

  return (
    <div className="space-y-5" data-testid="qualiopi-dashboard">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-[18px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Dashboard Qualiopi global</h2>
          <p className="text-[12.5px] text-[#6B6C85] mt-0.5">Vue agrégée NPS, taux de réponse et points d'amélioration · prêt pour audit</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#F6F7FB] rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                data-testid={`period-${p.value}`}
                onClick={() => setPeriod(p.value)}
                className={`px-3 h-8 text-[12px] font-semibold rounded-md transition-colors ${
                  period === p.value ? 'bg-white text-[#1A1B2E] shadow-sm' : 'text-[#6B6C85] hover:text-[#1A1B2E]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            data-testid="export-pdf-btn"
            onClick={handleExportPdf}
            disabled={exporting || loading}
            className="h-9 px-3.5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 shadow-sm hover:opacity-95 disabled:opacity-60"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export PDF audit
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" />
        </div>
      )}

      {data && (
        <>
          {/* Top metrics row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={BarChart3} label="Questionnaires envoyés" value={g.total_sent} hint={data.period_label} />
            <MetricCard icon={Activity} label="Réponses reçues" value={g.total_received} color="#14B8A6" />
            <MetricCard
              icon={TrendingUp}
              label="Taux de réponse"
              value={`${g.response_rate}%`}
              color={g.response_rate >= 60 ? '#0F766E' : g.response_rate >= 30 ? '#F59E0B' : '#EF4444'}
              hint={g.response_rate >= 60 ? 'Bonne participation' : g.response_rate >= 30 ? 'Participation moyenne' : 'À améliorer'}
            />
            <MetricCard icon={Smile} label="Satisfaction moy." value={g.satisfaction_avg ? `${g.satisfaction_avg}%` : '—'} color="#0F766E" hint={`${g.samples} réponse${g.samples > 1 ? 's' : ''} d'échelle`} />
          </div>

          {/* NPS + breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <NPSGauge nps={g.nps} samples={g.samples} />
            <PromoterBreakdown promoters={g.promoters} passives={g.passives} detractors={g.detractors} samples={g.samples} />
          </div>

          {/* By programme */}
          <div className="soft-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Par programme</h3>
                <p className="text-[12px] text-[#8E8FA5]">Top 8 par nombre de réponses</p>
              </div>
            </div>
            <Table
              headers={['Programme', 'Envoyés', 'Reçus', 'Taux', 'NPS', 'Satisfaction']}
              rows={programmeRows.slice(0, 8)}
              empty="Aucune donnée sur cette période"
            />
          </div>

          {/* By trainer */}
          <div className="soft-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Par formateur</h3>
                <p className="text-[12px] text-[#8E8FA5]">Top 8 par nombre de réponses</p>
              </div>
            </div>
            <Table
              headers={['Formateur', 'Sessions', 'Envoyés', 'Reçus', 'Taux', 'NPS', 'Satisf.']}
              rows={trainerRows.slice(0, 8)}
              empty="Aucune donnée sur cette période"
            />
          </div>

          {/* Top irritants */}
          <div className="soft-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
              <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Points d'amélioration prioritaires</h3>
            </div>
            {(data.top_irritants || []).length === 0 ? (
              <div className="text-center py-6 text-[13px] text-[#8E8FA5]">Aucun point d'amélioration significatif détecté ✓</div>
            ) : (
              <div className="space-y-2">
                {data.top_irritants.map((it) => (
                  <div key={`${it.questionnaire_id}-${it.question_id}`} className="flex items-center gap-3 p-3 rounded-xl border border-[#FECACA] bg-[#FEF2F2]">
                    <div className="w-10 h-10 rounded-lg bg-[#FEE2E2] flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{it.label}</div>
                      <div className="text-[11.5px] text-[#8E8FA5]">{it.questionnaire_title} · {it.count} réponse{it.count > 1 ? 's' : ''}</div>
                    </div>
                    <div className="text-[18px] font-bold text-[#EF4444]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{it.avg_pct}%</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="text-[11px] text-[#8E8FA5] text-center pb-4">
            NPS = % promoteurs (≥ 80% du score max) − % détracteurs (&lt; 60%) · Plage : -100 à +100
          </div>
        </>
      )}
    </div>
  );
}
